import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export interface HeraldCardCatalogTableRegistration {
  registerCatalogTable: () => void;
  unregisterCatalogTable: () => void;
}

const HeraldCardCatalogTableRegistrationContext =
  createContext<HeraldCardCatalogTableRegistration | null>(null);

export const useOptionalHeraldCardCatalogTableRegistration =
  (): HeraldCardCatalogTableRegistration | null =>
    useContext(HeraldCardCatalogTableRegistrationContext);

interface HeraldCardCatalogTableDescendantProviderProps {
  readonly children: ReactNode;
  readonly onHasCatalogTableChange: (present: boolean) => void;
}

/** Wraps a catalog Card PNG export subtree so descendant catalog tables can flip the parent toolbar to CSV. */
export const HeraldCardCatalogTableDescendantProvider = ({
  children,
  onHasCatalogTableChange,
}: HeraldCardCatalogTableDescendantProviderProps) => {
  const [count, setCount] = useState(0);

  const registerCatalogTable = useCallback(() => {
    setCount((c) => c + 1);
  }, []);

  const unregisterCatalogTable = useCallback(() => {
    setCount((c) => Math.max(0, c - 1));
  }, []);

  const value = useMemo(
    (): HeraldCardCatalogTableRegistration => ({
      registerCatalogTable,
      unregisterCatalogTable,
    }),
    [registerCatalogTable, unregisterCatalogTable],
  );

  useEffect(() => {
    onHasCatalogTableChange(count > 0);
  }, [count, onHasCatalogTableChange]);

  return (
    <HeraldCardCatalogTableRegistrationContext.Provider value={value}>
      {children}
    </HeraldCardCatalogTableRegistrationContext.Provider>
  );
};
